import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './lib/supabaseClient';
import { AccessProvider, useAccess } from './lib/access';
import Dashboard from './pages/Dashboard';
import Vendors from './pages/Vendors';
import Calendar from './pages/Calendar';
import Templates from './pages/Templates';
import Jobs from './pages/Jobs';
import Revenue from './pages/Revenue';
import PropertyHub from './pages/PropertyHub';
import MyWork from './pages/MyWork';
import Admin from './pages/Admin';
import NoAccess from './pages/NoAccess';
import Login from './pages/Login';
import './App.css';

const viewFromPath = () => (window.location.pathname.replace(/\/+$/, '') === '/admin' ? 'admin' : null);

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const recordedFor = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') recordedFor.current = null;
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Sign-in history: once per new session, ask the database to record it. record_signin() reads the
  // method and time itself and ignores sessions it has already recorded.
  const userId = session?.user?.email_confirmed_at ? session.user.id : null;
  useEffect(() => {
    if (!userId || recordedFor.current === userId) return;
    recordedFor.current = userId;
    supabase.rpc('record_signin').then(({ error }) => {
      if (error) console.warn('record_signin failed', error.message);
    });
  }, [userId, session]);

  if (loading) return <div className="loading">Loading...</div>;

  if (!session) return <Login />;

  // Signed in but email not verified yet: RLS would return nothing, so finish verification first.
  if (!session.user?.email_confirmed_at) {
    return <Login key="verify" pendingEmail={session.user?.email} />;
  }

  return (
    <AccessProvider userKey={session.user.id} fallback={<div className="loading">Loading...</div>}>
      <Routes session={session} />
    </AccessProvider>
  );
}

function Routes({ session }) {
  const access = useAccess();
  const [currentView, setCurrentView] = useState(() => viewFromPath() || access.homeView);
  const [hubPropertyId, setHubPropertyId] = useState(null);

  useEffect(() => {
    const onPop = () => setCurrentView(viewFromPath() || access.homeView);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [access.homeView]);

  const handleNavigate = (view, propertyId) => {
    setCurrentView(view);
    if (view === 'property-hub' && propertyId) {
      setHubPropertyId(propertyId);
    }
    const path = view === 'admin' ? '/admin' : '/';
    if (window.location.pathname !== path) window.history.pushState(null, '', path);
  };

  if (!access.hasAccess) return <NoAccess session={session} error={access.error} />;

  // Only views this role can use; anything else (including a typed /admin) falls back to home.
  const view = access.views.has(currentView) ? currentView : access.homeView;
  const props = { session, currentView: view, onNavigate: handleNavigate };

  if (view === 'my-work') return <MyWork {...props} />;
  if (view === 'admin') return <Admin {...props} />;
  if (view === 'property-hub' && hubPropertyId) return <PropertyHub {...props} propertyId={hubPropertyId} />;
  if (view === 'vendors') return <Vendors {...props} />;
  if (view === 'calendar') return <Calendar {...props} />;
  if (view === 'templates') return <Templates {...props} />;
  if (view === 'jobs') return <Jobs {...props} />;
  if (view === 'revenue') return <Revenue {...props} />;
  return <Dashboard {...props} />;
}

export default App;
